/* eslint-disable max-len*/
import React, {useContext, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import clsx from 'clsx';

import {USER_ROLES} from '../../../website-ui/constants/user';
import {useUser} from '../../../website-ui/hooks/use-user';
import {
  CALLBACK_URL,
  DEFAULT_VOTES,
  JWT,
  MODAL_ERROR,
  RATING_API,
  SUPPRESS_CONFIRM,
  VOTE_CONFIRM,
  VOTE_NEGATIVE,
  VOTE_POSITIVE,
  VOTE_SUCCESS} from '../../constants';
import {RecaptchaContext} from '../../contexts/RecaptchaContext';
import {UserVotesContext} from '../../contexts/UserVotesContext';
import {isGolosNarodaActive} from '../../helpers';
import CenterModal from '../../modals/CenterModal';
import ConfirmVoteModalBody from '../../modals/ConfirmVoteModalBody';
import ErrorModalBody from '../../modals/ErrorModalBody';
import SuccessVotedModalBody from '../../modals/SuccessVotedModalBody';
import {AuthAlert} from '../AuthAlert';
import FadeInComponent from '../FadeIn';

import styles from './index.module.css';

export default ({
  entityId,
  sendingVote,
  setSendingVote,
  entityName,
  currentNominationId,
  candidateVotes = {positive: DEFAULT_VOTES, negative: DEFAULT_VOTES},
  className,
  onVoteUnlogged
}) => {
  const {initialUserVotes,
    setInitialUserVotes,
    nominationVotes,
    setNominationVotes,
    setCandidates} = useContext(UserVotesContext);
  const [votes, setVotes] = useState(candidateVotes);
  const [openModal, setOpenModal] = useState({isOpen: false});
  const {isAuthenticated, role} = useUser();
  const isLogged = isAuthenticated && role === USER_ROLES.EXTENDED;
  const isSuppressConfirm = !!JSON.parse(localStorage.getItem(SUPPRESS_CONFIRM) || null);
  const {category, nomination} = useParams();
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const callbackUrl = category ? `/${category}/${nomination ? `${nomination}/` : ''}` : '/';
  const {captchaProps} = useContext(RecaptchaContext);
  const classNames = clsx({
    [styles.voteButtonWrapper]: !className,
    [className]: Boolean(className)
  });
  const containerRef = useRef();
  const sendVote = async (type, _entityId) => {
    const vote = type === VOTE_POSITIVE ? 1 : -1;
    const token = await captchaProps?.get();

    setSendingVote(true);
    fetch(`${RATING_API}votes`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(JWT)}`
        },
        body: JSON.stringify({
          count: vote,
          candidate: _entityId,
          nomination: currentNominationId,
          token
        })
      })
      .then(data => data.json())
      .then(data => {
        setCandidates({nominationId: data.nomination.id, list: data.nomination.candidates});
        setOpenModal({isOpen: true, type: VOTE_SUCCESS, voteType: type, name: entityName});
        setVotes({...votes, [type]: votes[type] + 1});
        setNominationVotes({
          ...nominationVotes,
          [type]: entityId
        });
        setInitialUserVotes([
          ...initialUserVotes,
          {
            candidate: _entityId,
            count: vote,
            nomination: currentNominationId
          }
        ]);
      })
      .catch(err => {
        setOpenModal({isOpen: true, type: MODAL_ERROR});
        console.error(err);
      })
      .finally(() => {
        setSendingVote(false);
      });
  };
  const voteButtonHandler = (type, _entityId) => {
    if (!isLogged) {
      if (typeof onVoteUnlogged === 'function') {
        return onVoteUnlogged();
      }
      containerRef.current?.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
      return setShowAuthPopup(true);
    }
    if (isLogged && !isSuppressConfirm && !openModal.isOpen) {
      return setOpenModal({isOpen: true, type: VOTE_CONFIRM, voteType: type, _entityId});
    }
    if (isLogged) {
      return sendVote(type, _entityId);
    }
    localStorage.setItem(CALLBACK_URL, callbackUrl);
    return setOpenModal({isOpen: true});
  };
  const renderModal = () => {
    switch (openModal.type) {
      case MODAL_ERROR: return <ErrorModalBody />;
      case VOTE_SUCCESS: return <SuccessVotedModalBody openModal={openModal} />;
      case VOTE_CONFIRM: return <ConfirmVoteModalBody voteType={openModal.voteType} _entityId={openModal._entityId} sendVote={sendVote} setModal={setOpenModal} />;
      default: return null;
    }
  };

  const renderModalType = () => (
    <CenterModal isOpen={openModal.isOpen} setModal={setOpenModal}>
      {renderModal()}
    </CenterModal>
  );

  const votedType = Object.keys(nominationVotes).find(key => nominationVotes[key] === entityId);
  const getListOfVotedIds = currentNominationVotes => {
    let arrayOfVotedIds = [];

    for (const key in currentNominationVotes) {
      if (currentNominationVotes[key] !== null) {
        arrayOfVotedIds = [...arrayOfVotedIds, key];
      }
    }
    return arrayOfVotedIds;
  };

  return (
    <div className={classNames} ref={containerRef}>
      <FadeInComponent
        className={styles.authAlertWrapper}
        handleExit={() => setShowAuthPopup(false)}
        open={showAuthPopup}
      >
        <AuthAlert withToolTip={false} withCloseIcon onClose={() => setShowAuthPopup(false)} />
      </FadeInComponent>
      <button
        disabled={sendingVote || getListOfVotedIds(nominationVotes).includes(VOTE_POSITIVE) || !isGolosNarodaActive}
        className={clsx({
          [styles.voteButton]: true,
          [styles.votePositive]: true,
          [styles[votedType]]: votedType,
          [styles.disabledForHover]: !isLogged
        })}
        onClick={() => voteButtonHandler(VOTE_POSITIVE, entityId)}
      >
        <div className={styles.iconWrapper}>
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.3012 25.6245H10.375C9.82272 25.6245 9.375 25.1768 9.375 24.6245V11.8166C9.375 11.5847 9.45562 11.3599 9.60307 11.1809L15.2083 4.37451H18.1696C18.9026 4.37451 19.3865 5.13706 19.0744 5.80031L15.8565 12.6384H21.3657H25.875C26.4273 12.6384 26.875 13.0861 26.875 13.6384V15.5898L23.2336 24.9859C23.0844 25.3708 22.714 25.6245 22.3012 25.6245Z" stroke="#757575" strokeWidth="2.7357" strokeLinejoin="round" />
            <path d="M5.625 11.875V25.625H3.125V11.875H5.625Z" stroke="#757575" strokeWidth="2.7357" strokeLinejoin="round" />
          </svg>
        </div>
        <span className={styles.voteNumbers}>
          {votes.positive}
        </span>
      </button>
      <button
        disabled={sendingVote || getListOfVotedIds(nominationVotes).includes(VOTE_NEGATIVE) || !isGolosNarodaActive}
        className={clsx({
          [styles.voteButton]: true,
          [styles.voteNegative]: true,
          [styles[votedType]]: votedType,
          [styles.disabledForHover]: !isLogged
        })}
        onClick={() => voteButtonHandler(VOTE_NEGATIVE, entityId)}
      >
        <div className={styles.iconWrapper}>
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.3012 4.375H10.375C9.82272 4.375 9.375 4.82271 9.375 5.375V18.1829C9.375 18.4148 9.45562 18.6396 9.60307 18.8186L15.2083 25.625H18.1696C18.9026 25.625 19.3865 24.8624 19.0744 24.1992L15.8565 17.3611H21.3657H25.875C26.4273 17.3611 26.875 16.9134 26.875 16.3611V14.4097L23.2336 5.01364C23.0844 4.6287 22.714 4.375 22.3012 4.375Z" stroke="#757575" strokeWidth="2.7357" strokeLinejoin="round" />
            <path d="M5.625 18.1245V4.37451H3.125V18.1245H5.625Z" stroke="#757575" strokeWidth="2.7357" strokeLinejoin="round" />
          </svg>
        </div>
        <span className={styles.voteNumbers}>
          {votes.negative}
        </span>
      </button>
      {renderModalType()}
    </div>
  );
};
